/**
 * Cấu hình 4 tab của trang chi tiết user: Đơn hàng / Rút tiền / Thưởng / Giới thiệu.
 *
 * Mỗi tab chỉ là (fetcher + columns) nạp vào UserDetailTable. Tất cả đều dùng LẠI
 * endpoint sẵn có kèm filter userId — không thêm API mới:
 *   - đơn hàng : GET /user/all_order?userId=
 *   - rút tiền : GET /payment?userId=&paymentType=withdraw
 *   - thưởng   : GET /bonus/bonus_history?userId=
 *   - giới thiệu: GET /user/refs-by-user/:userId
 */

import React from "react";
import { Tag } from "antd";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import authApi from "../../../api/authApi";
import paymentApi from "../../../api/paymentApi";

const vnd = (v) => `${Number(v || 0).toLocaleString("vi-VN")}đ`;

/** Mốc thời gian trong DB lẫn lộn giây và milli giây -> tự suy ra đơn vị. */
const ts = (v) => {
    if (!v) return "—";
    const ms = v < 946684800000 ? v * 1000 : v;
    return format(new Date(ms), "dd/MM/yyyy HH:mm");
};

/** Trạng thái đối soát đơn (conversionStatus) — khớp nhãn ở trang /order */
const ORDER_STATUS_TAG = {
    1: { color: "orange", text: "Chờ duyệt" },
    2: { color: "green", text: "Đã duyệt" },
    3: { color: "default", text: "Đã huỷ" },
    4: { color: "orange", text: "Chờ duyệt" },
};

/** Trạng thái lệnh rút (Payments.status) — khớp nhãn ở trang /withdraw */
const WITHDRAW_STATUS_TAG = {
    1: { color: "orange", text: "Chờ duyệt" },
    2: { color: "green", text: "Đã xử lý" },
    3: { color: "default", text: "Đã huỷ" },
};

const ORDER_COLUMNS = [
    { title: "Mã đơn", dataIndex: "orderId", key: "orderId", width: 180, render: (v) => <span className="font-mono text-xs">{v}</span> },
    { title: "Sàn", dataIndex: "campId", key: "campId", width: 80, render: (v) => v || "—" },
    { title: "Trạng thái sàn", dataIndex: "orderStatus", key: "orderStatus", width: 130, ellipsis: true },
    {
        title: "Đối soát", dataIndex: "conversionStatus", key: "conversionStatus", width: 110,
        render: (v) => {
            const t = ORDER_STATUS_TAG[v];
            return t ? <Tag color={t.color}>{t.text}</Tag> : v ?? "—";
        },
    },
    { title: "HH sàn", dataIndex: "commission", key: "commission", align: "right", width: 110, render: vnd },
    {
        title: "HH user", dataIndex: "userCommission", key: "userCommission", align: "right", width: 110,
        render: (v) => <span className="font-semibold text-green-700">{vnd(v)}</span>,
    },
    {
        title: "Đã trả", dataIndex: "payedToUser", key: "payedToUser", width: 90, align: "center",
        render: (v) => (v ? <Tag color="green">Đã trả</Tag> : <Tag>Chưa</Tag>),
    },
    { title: "Ngày mua", dataIndex: "purchaseTime", key: "purchaseTime", width: 145, render: ts },
];

const WITHDRAW_COLUMNS = [
    { title: "ID", dataIndex: "paymentId", key: "paymentId", width: 80, render: (v) => <span className="font-mono text-xs">#{v}</span> },
    {
        title: "Số tiền", dataIndex: "amount", key: "amount", align: "right", width: 130,
        render: (v) => <span className="font-semibold">{vnd(v)}</span>,
    },
    {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 110,
        render: (v) => {
            const t = WITHDRAW_STATUS_TAG[v];
            return t ? <Tag color={t.color}>{t.text}</Tag> : v ?? "—";
        },
    },
    {
        title: "Tài khoản nhận", key: "bank", width: 240, ellipsis: true,
        render: (_, r) => (
            <span className="text-xs">
                {r.bankAccountNumber || "—"} · {r.bankAccountName || "—"}
                {r.Bank?.bankName ? ` · ${r.Bank.bankName}` : ""}
            </span>
        ),
    },
    { title: "Ghi chú", dataIndex: "note", key: "note", ellipsis: true, render: (v) => v || "—" },
    { title: "Ngày tạo", dataIndex: "createTime", key: "createTime", width: 145, render: ts },
    { title: "Ngày xử lý", dataIndex: "completeTime", key: "completeTime", width: 145, render: ts },
];

const BONUS_COLUMNS = [
    { title: "ID", dataIndex: "bonusId", key: "bonusId", width: 80, render: (v) => `#${v}` },
    {
        title: "Loại", dataIndex: "bonusCash", key: "type", width: 100,
        render: (v) => (v < 0 ? <Tag color="red">Trừ tiền</Tag> : <Tag color="green">Thưởng</Tag>),
    },
    {
        title: "Số tiền", dataIndex: "bonusCash", key: "bonusCash", align: "right", width: 130,
        render: (v) => (
            <span className={v < 0 ? "text-red-500 font-semibold" : "text-green-600 font-semibold"}>
                {v < 0 ? "−" : "+"}{vnd(Math.abs(v))}
            </span>
        ),
    },
    {
        title: "Admin", key: "admin", width: 160, ellipsis: true,
        render: (_, r) => (r.bonusAdmin ? `${r.bonusAdmin.name} (#${r.adminId})` : r.adminId ? `#${r.adminId}` : "—"),
    },
    { title: "Lý do", dataIndex: "note", key: "note", ellipsis: true, render: (v) => v || "—" },
    { title: "Thời gian", dataIndex: "createTime", key: "createTime", width: 145, render: ts },
];

const REF_COLUMNS = [
    {
        title: "ID", dataIndex: "userId", key: "userId", width: 80,
        render: (v) => <Link to={`/user/${v}`}>#{v}</Link>,
    },
    { title: "Tên", dataIndex: "name", key: "name", ellipsis: true },
    { title: "Email", dataIndex: "email", key: "email", ellipsis: true, width: 210 },
    { title: "Đơn hoàn tất", dataIndex: "completedOrders", key: "completedOrders", align: "right", width: 120 },
    { title: "Tổng đơn", dataIndex: "totalOrders", key: "totalOrders", align: "right", width: 100 },
    { title: "HH đã duyệt", dataIndex: "approvedCommission", key: "approvedCommission", align: "right", width: 130, render: vnd },
    { title: "HH chờ", dataIndex: "pendingCommission", key: "pendingCommission", align: "right", width: 120, render: vnd },
    { title: "Ngày đăng ký", dataIndex: "createTime", key: "createTime", width: 145, render: ts },
];

/**
 * Trả về 4 tab (key/label/columns/rowKey/fetcher/perms) cho 1 userId.
 *
 * `perms` = quyền backend của endpoint tab đó. CTV (mod) chỉ có quyền "users" sẽ
 * không thấy tab rút tiền/thưởng thay vì bấm vào rồi ăn 403.
 */
export function buildUserDetailTabs(userId) {
    return [
        {
            key: "orders",
            label: "Đơn hàng",
            perms: ["orders"],
            columns: ORDER_COLUMNS,
            rowKey: "orderId",
            fetcher: async ({ page, limit }) => {
                const { data } = await authApi.getAllOrder(`userId=${userId}&page=${page}&limit=${limit}&sort=-purchaseTime`);
                return { rows: data.allOrder || [], total: data.totalOrder || 0 };
            },
        },
        {
            key: "withdraw",
            label: "Lịch sử rút tiền",
            perms: ["finance", "dashboard"], // GET /payment cho phép cả quyền dashboard
            columns: WITHDRAW_COLUMNS,
            rowKey: "paymentId",
            fetcher: async ({ page, limit }) => {
                const { data } = await paymentApi.getAll(
                    `userId=${userId}&paymentType=withdraw&page=${page}&limit=${limit}&sort=-createTime`
                );
                return { rows: data.data?.payments || [], total: data.data?.totalPayment || 0 };
            },
        },
        {
            key: "bonus",
            label: "Lịch sử thưởng / trừ tiền",
            perms: ["finance"],
            columns: BONUS_COLUMNS,
            rowKey: "bonusId",
            fetcher: async ({ page, limit }) => {
                const { data } = await authApi.getBonusHistory({ userId, page, limit });
                return { rows: data.data || [], total: data.total || 0 };
            },
        },
        {
            key: "refs",
            label: "Người được giới thiệu",
            perms: ["referral"],
            columns: REF_COLUMNS,
            rowKey: "userId",
            fetcher: async ({ page, limit }) => {
                const { data } = await authApi.getRefsByUserId(userId, `page=${page}&limit=${limit}`);
                return { rows: data.refUsers || [], total: data.totalCount || 0 };
            },
        },
    ];
}
